import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Alert, AlertDescription } from "../ui/alert";
import usePoll from "../../hooks/usePoll";
import { playApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import ChoiceTiles from "./ChoiceTiles";
import Countdown from "./Countdown";
import Creature from "./Creature";
import usePointer from "./usePointer";
import { ordinal } from "./game";
import {
  caretPoint,
  clearSession,
  loadSession,
  normalizeCode,
  saveSession,
} from "./playSession";

/**
 * /play — a player's phone (projects/trivia). Outside the app shell and
 * sign-in: players have no mono account. Join with the code on the big
 * screen and a nickname; after that the page asks the server once a
 * second what to show, and sends one answer per question.
 */
function TriviaPlay() {
  const [params] = useSearchParams();
  // A link to a different game (?code=) means a new game: show the join
  // form for it, not the last game's final screen.
  const [session, setSession] = useState(() => {
    const saved = loadSession();
    const wanted = normalizeCode(params.get("code"));
    return saved && (!wanted || wanted === saved.code) ? saved : null;
  });
  const [game, setGame] = useState(null);
  const [notice, setNotice] = useState(null);

  function joined(token, state) {
    const next = { token, code: state.code };
    saveSession(next);
    setSession(next);
    setGame(state);
    setNotice(null);
  }

  function leave(message = null) {
    // Only this game's token: another tab may have joined a newer game.
    clearSession(session?.token);
    setSession(null);
    setGame(null);
    setNotice(message);
  }

  return (
    <div className="flex min-h-dvh flex-col bg-background text-foreground">
      <header className="flex items-center justify-between border-b px-4 py-3">
        <span className="font-display text-lg font-semibold">AI Term Trivia</span>
        {/* Only once the game is over: mid-game there's no way back in
            (joining closes at Start), so there's no button to lose it. */}
        {session && game?.phase === "finished" && (
          <Button variant="outline" className="h-11" onClick={() => leave()}>
            Join another game
          </Button>
        )}
      </header>
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col gap-5 p-4">
        {session ? (
          <PlayerGame
            token={session.token}
            game={game}
            setGame={setGame}
            onGone={leave}
          />
        ) : (
          <JoinForm onJoined={joined} notice={notice} />
        )}
      </main>
    </div>
  );
}

/** The code and nickname, with a creature watching what you type. */
function JoinForm({ onJoined, notice }) {
  const [params] = useSearchParams();
  const pointer = usePointer();
  const [code, setCode] = useState(() => normalizeCode(params.get("code")).slice(0, 8));
  const [nickname, setNickname] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [caret, setCaret] = useState(null);
  const buttonRef = useRef(null);
  // A creature to keep you company; your own is picked when you join.
  const [kind] = useState(() => Math.floor(Math.random() * 6));

  const follow = (event) => setCaret(caretPoint(event.target));
  const ready = code.trim().length >= 6 && nickname.trim().length > 0;
  let look = caret ?? pointer;
  if (!caret && ready && buttonRef.current) {
    const r = buttonRef.current.getBoundingClientRect();
    look = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { data } = await playApi.join(code, nickname);
      onJoined(data.token, data.state);
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't join. Check the code and try again."));
      setBusy(false);
    }
  }

  const inputEvents = {
    onFocus: follow,
    onKeyUp: follow,
    onClick: follow,
    onSelect: follow,
    onBlur: () => setCaret(null),
  };

  return (
    <form className="flex flex-col gap-5" onSubmit={submit} noValidate>
      <div className="flex justify-center pt-2">
        <Creature kind={kind} size={150} look={look} mood={busy ? "wait" : "idle"} />
      </div>
      {notice && (
        <Alert>
          <AlertDescription>{notice}</AlertDescription>
        </Alert>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="trivia-code">Game code</Label>
        <Input
          id="trivia-code"
          value={code}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase().slice(0, 8));
            follow(e);
          }}
          placeholder="K7QM2P"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          className="h-12 font-mono text-2xl tracking-widest"
          aria-describedby="trivia-code-help"
          {...inputEvents}
        />
        <p id="trivia-code-help" className="text-sm text-muted-foreground">
          The six letters and numbers on the big screen.
        </p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="trivia-nickname">Your name</Label>
        <Input
          id="trivia-nickname"
          value={nickname}
          onChange={(e) => {
            // 20 characters as the server counts them (code points), so an
            // emoji is never cut in half.
            setNickname(Array.from(e.target.value).slice(0, 20).join(""));
            follow(e);
          }}
          placeholder="What the scoreboard calls you"
          autoComplete="nickname"
          className="h-12 text-lg"
          aria-describedby="trivia-nickname-help"
          {...inputEvents}
        />
        <p id="trivia-nickname-help" className="text-sm text-muted-foreground">
          Up to 20 characters. Everyone sees it on the big screen.
        </p>
      </div>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Button ref={buttonRef} type="submit" className="h-12 text-lg" disabled={busy || !ready}>
        {busy ? "Joining…" : "Join the game"}
      </Button>
    </form>
  );
}

/** One player's view of the game, polled once a second. */
function PlayerGame({ token, game, setGame, onGone }) {
  const pointer = usePointer();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const generation = useRef(0);
  // An answer's response that lands after this game was left must not
  // repaint the page with it.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const tick = useCallback(
    async ({ stale }) => {
      const asked = generation.current;
      try {
        const { data } = await playApi.state(token);
        if (stale() || asked !== generation.current) return;
        setGame(data);
      } catch (err) {
        if (stale()) return;
        if (err?.response?.status === 404) {
          onGone(apiErrorMessage(err, "That game has ended."));
          return;
        }
        throw err;
      }
    },
    [token, setGame, onGone],
  );
  const { status } = usePoll(tick, {
    intervalMs: 1000,
    immediate: true,
    resetKey: token,
    enabled: game?.phase !== "finished",
  });

  // A new question clears the last one's error.
  const index = game?.index;
  useEffect(() => setError(null), [index]);

  async function pick(letter) {
    setBusy(true);
    setError(null);
    try {
      const { data } = await playApi.answer(token, game.index, letter);
      if (!alive.current) return;
      generation.current += 1;
      setGame(data);
    } catch (err) {
      if (!alive.current) return;
      setError(apiErrorMessage(err, "Your answer didn't go through. Try again."));
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  if (!game) {
    return <p className="text-muted-foreground">Finding your game…</p>;
  }

  const me = game.me;
  const lastQuestion = game.question_number >= game.question_count;
  const comingUp = lastQuestion ? "The final scores are next." : "The next question is coming up.";
  const reconnecting = status === "reconnecting" && (
    <p className="text-sm text-muted-foreground" role="status">
      Reconnecting…
    </p>
  );

  if (game.phase === "lobby") {
    return (
      <section className="flex flex-col items-center gap-4 pt-6 text-center" aria-label="Lobby">
        <Creature kind={me.creature} size={180} look={pointer} mood="wait" />
        <h1 className="font-display text-3xl font-semibold">You&apos;re in, {me.nickname}!</h1>
        <p className="text-lg">Waiting for the host to start. Keep this page open.</p>
        <p className="text-muted-foreground" role="status">
          {game.player_count} {game.player_count === 1 ? "player" : "players"} so far
        </p>
        {reconnecting}
      </section>
    );
  }

  if (game.phase === "question") {
    const answered = me.answer !== null;
    return (
      <section className="flex flex-col gap-4" aria-label={`Question ${game.question_number}`}>
        <p className="text-muted-foreground">
          Question {game.question_number} of {game.question_count}
        </p>
        <Countdown
          secondsLeft={game.seconds_left}
          total={game.seconds_per_question}
          questionKey={game.index}
        />
        {game.question && (
          <>
            <h1 className="text-xl leading-snug font-semibold">{game.question.prompt}</h1>
            <ChoiceTiles
              choices={game.question.choices}
              picked={me.answer}
              onPick={answered ? null : pick}
              disabled={busy || answered}
            />
          </>
        )}
        {answered && (
          <div className="flex items-center gap-3" role="status">
            <Creature kind={me.creature} size={64} mood="wait" />
            <p className="text-lg">Locked in: {me.answer}. Waiting for the others…</p>
          </div>
        )}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {reconnecting}
      </section>
    );
  }

  if (game.phase === "reveal" && !game.question) {
    return (
      <section className="flex flex-col items-center gap-4 pt-6 text-center" aria-label="Answer">
        <Creature kind={me.creature} size={110} look={pointer} />
        <h1 className="font-display text-2xl font-semibold">
          That question was taken out of the game
        </h1>
        <p className="text-lg">No points either way. {comingUp}</p>
        {reconnecting}
      </section>
    );
  }

  if (game.phase === "reveal") {
    const verdict =
      me.answer === null ? "No answer" : me.is_correct ? "Right!" : "Not this time";
    return (
      <section className="flex flex-col gap-4" aria-label="Answer">
        <div className="flex items-center gap-4">
          <Creature kind={me.creature} size={110} mood={me.is_correct ? "happy" : "sad"} />
          <div>
            <h1 className="font-display text-3xl font-semibold">{verdict}</h1>
            <p className="text-lg">
              {me.points > 0 ? `+${me.points} points` : "0 points"}
            </p>
          </div>
        </div>
        <p className="rounded-lg border bg-card p-3 text-lg">
          Score <strong className="tabular-nums">{me.score}</strong> ·{" "}
          {ordinal(me.rank)} of {game.player_count}
        </p>
        {game.question && game.reveal && (
          <ChoiceTiles
            choices={game.question.choices}
            correct={game.reveal.correct}
            picked={me.answer}
          />
        )}
        {game.reveal?.explanation && <p className="text-muted-foreground">{game.reveal.explanation}</p>}
        <p className="text-sm text-muted-foreground">{comingUp}</p>
        {reconnecting}
      </section>
    );
  }

  return (
    <section className="flex flex-col items-center gap-4 pt-6 text-center" aria-label="Final scores">
      <Creature kind={me.creature} size={180} look={pointer} mood={me.rank <= 3 ? "happy" : "idle"} />
      <h1 className="font-display text-3xl font-semibold">
        You finished {ordinal(me.rank)} of {game.player_count}
      </h1>
      <p className="text-xl">
        <span className="tabular-nums">{me.score}</span> points
      </p>
      <p className="text-muted-foreground">Thanks for playing!</p>
    </section>
  );
}

JoinForm.propTypes = {
  onJoined: PropTypes.func.isRequired,
  notice: PropTypes.string,
};

PlayerGame.propTypes = {
  token: PropTypes.string.isRequired,
  game: PropTypes.object,
  setGame: PropTypes.func.isRequired,
  onGone: PropTypes.func.isRequired,
};

export default TriviaPlay;
